import React from 'react';
import PropTypes from 'prop-types';

import { StudyItem } from '../StudyItem';
import { StudyBrowserSort } from '../StudyBrowserSort';
import { StudyBrowserViewOptions } from '../StudyBrowserViewOptions';
import { ScrollArea } from '../ScrollArea';
import { ThumbnailList } from '../ThumbnailList';

const noop = () => {};

const StudyBrowser = ({
  tabs,
  activeTabName,
  expandedStudyInstanceUIDs,
  onClickTab = noop,
  onClickStudy = noop,
  onClickThumbnail = noop,
  onDoubleClickThumbnail = noop,
  onClickUntrack = noop,
  activeDisplaySetInstanceUIDs,
  servicesManager,
  showSettings,
  viewPresets,
  ThumbnailMenuItems,
  StudyMenuItems,
}: withAppTypes) => {
  const getTabContent = () => {
    const tabData = (tabs as any)?.find((tab: any) => tab.name === activeTabName);
    const viewPreset = viewPresets
      ? (viewPresets as any)?.filter((preset: any) => preset.selected)[0]?.id
      : 'thumbnails';

    // In mobile mode, flatten all displaySets from all studies into a single list
    if (isMobileUI) {
      const allDisplaySets =
        (tabData?.studies as any)?.flatMap((study: any) => study.displaySets || []) || [];
      return (
        <ThumbnailList
          thumbnails={allDisplaySets}
          activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs as any}
          onThumbnailClick={onClickThumbnail as any}
          onThumbnailDoubleClick={onDoubleClickThumbnail as any}
          onClickUntrack={onClickUntrack as any}
          viewPreset={viewPreset}
          ThumbnailMenuItems={ThumbnailMenuItems as any}
        />
      );
    }

    // Desktop mode: show studies with accordion
    return (tabData?.studies as any)?.map(
      ({ studyInstanceUid, date, description, numInstances, modalities, displaySets }: any) => {
        const isExpanded = (expandedStudyInstanceUIDs as any)?.includes(studyInstanceUid);
        return (
          <React.Fragment key={studyInstanceUid}>
            <StudyItem
              date={date}
              description={description}
              numInstances={numInstances}
              isExpanded={isExpanded}
              displaySets={displaySets}
              modalities={modalities}
              isActive={isExpanded}
              onClick={() => (onClickStudy as any)(studyInstanceUid)}
              onClickThumbnail={onClickThumbnail}
              onDoubleClickThumbnail={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              data-cy="thumbnail-list"
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
              StudyMenuItems={StudyMenuItems}
              StudyInstanceUID={studyInstanceUid}
            />
          </React.Fragment>
        );
      }
    );
  };

  const detectMobile = () => {
    try {
      const ua = (
        navigator.userAgent ||
        navigator.vendor ||
        (window as any).opera ||
        ''
      ).toLowerCase();
      const isTouch = typeof window !== 'undefined' && 'ontouchstart' in window;
      const isCoarse =
        typeof window !== 'undefined' &&
        !!window.matchMedia &&
        window.matchMedia('(pointer: coarse)').matches;
      const smallScreen =
        typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) <= 820;
      const uaMobile = /android|iphone|ipad|ipod|windows phone|blackberry/.test(ua);
      return uaMobile || isCoarse || (isTouch && smallScreen);
    } catch (e) {
      return false;
    }
  };
  const searchParams = new URLSearchParams(window.location.search);
  const mobileParam = (searchParams.get('mobile') || '').toLowerCase();
  const isMobileUI =
    mobileParam === '1' || mobileParam === 'true'
      ? true
      : mobileParam === '0' || mobileParam === 'false'
        ? false
        : detectMobile();

  return (
    <ScrollArea>
      <div
        className="bg-bkg-low flex flex-1 flex-col gap-[4px]"
        data-cy={'studyBrowser-panel'}
      >
        <div className="flex flex-col gap-[4px]">
          {showSettings && !isMobileUI && (
            <div className="w-100 bg-bkg-low flex h-[48px] items-center justify-center gap-[10px] px-[8px] py-[10px]">
              <>
                <StudyBrowserViewOptions
                  tabs={tabs}
                  onSelectTab={onClickTab}
                  activeTabName={activeTabName}
                />
                <StudyBrowserSort servicesManager={servicesManager} />
              </>
            </div>
          )}
          {getTabContent()}
        </div>
      </div>
    </ScrollArea>
  );
};

StudyBrowser.propTypes = {
  onClickTab: PropTypes.func.isRequired,
  onClickStudy: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  activeTabName: PropTypes.string.isRequired,
  expandedStudyInstanceUIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      studies: PropTypes.arrayOf(
        PropTypes.shape({
          studyInstanceUid: PropTypes.string.isRequired,
          date: PropTypes.string,
          numInstances: PropTypes.number,
          modalities: PropTypes.string,
          description: PropTypes.string,
          displaySets: PropTypes.arrayOf(
            PropTypes.shape({
              displaySetInstanceUID: PropTypes.string.isRequired,
              imageSrc: PropTypes.string,
              imageAltText: PropTypes.string,
              seriesDate: PropTypes.string,
              seriesNumber: PropTypes.any,
              numInstances: PropTypes.number,
              description: PropTypes.string,
              componentType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage'])
                .isRequired,
              isTracked: PropTypes.bool,
              /**
               * Data the thumbnail should expose to a receiving drop target. Use a matching
               * `dragData.type` to identify which targets can receive this draggable item.
               * If this is not set, drag-n-drop will be disabled for this thumbnail.
               *
               * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
               */
              dragData: PropTypes.shape({
                /** Must match the "type" a dropTarget expects */
                type: PropTypes.string.isRequired,
              }),
            })
          ),
        })
      ).isRequired,
    })
  ),
  StudyMenuItems: PropTypes.func,
};

export { StudyBrowser };
